import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getReceipt, deleteReceipt, pdfUrl } from "@/lib/api";
import { formatMoney, formatDate, formatDateTime, imgSrc, effectiveValue } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, FileDown, Trash2, Sparkles } from "lucide-react";
import { VatBreakdown } from "@/components/VatBreakdown";
import { toast } from "sonner";

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-2.5 border-b border-border last:border-0">
      <span className="data-label">{label}</span>
      <span className="text-sm font-medium text-right" data-testid={testId}>
        {value || "—"}
      </span>
    </div>
  );
}

export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setReceipt(await getReceipt(id));
      } catch {
        toast.error("Beleg konnte nicht geladen werden.");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleDelete = async () => {
    try {
      await deleteReceipt(id);
      toast.success("Beleg gelöscht.");
      navigate("/");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const downloadPdf = () => {
    const a = document.createElement("a");
    a.href = pdfUrl(id);
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="h-8 w-48 bg-muted animate-pulse mb-6 rounded-sm" />
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5 h-96 bg-muted animate-pulse rounded-sm" />
          <div className="md:col-span-7 h-96 bg-muted animate-pulse rounded-sm" />
        </div>
      </div>
    );
  }

  if (!receipt) return null;

  return (
    <div data-testid="receipt-detail">
      <button
        onClick={() => navigate("/")}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6 transition-colors"
        data-testid="detail-back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-medium">
            {receipt.supplier || "Beleg"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {formatDate(receipt.date)} · {formatMoney(receipt.price, receipt.currency)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate(`/beleg/${id}/bearbeiten`)}
            className="gap-2"
            data-testid="detail-edit-button"
          >
            <Pencil className="h-4 w-4" /> Bearbeiten
          </Button>
          <Button
            variant="secondary"
            onClick={downloadPdf}
            className="gap-2 border border-border"
            data-testid="detail-pdf-button"
          >
            <FileDown className="h-4 w-4" /> PDF
          </Button>
          <Button
            variant="ghost"
            onClick={() => setConfirmDelete(true)}
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="detail-delete-button"
          >
            <Trash2 className="h-4 w-4" /> Löschen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        {/* image */}
        <div className="md:col-span-5 md:sticky md:top-32">
          <div className="border border-border bg-card p-3">
            <div className="data-label mb-2 px-1">Originalbeleg</div>
            <div className="border border-border bg-muted rounded-sm overflow-hidden flex items-center justify-center min-h-[300px]">
              {receipt.image_base64 ? (
                <img
                  src={imgSrc(receipt.image_base64, receipt.image_mime)}
                  alt={receipt.supplier}
                  className="w-full h-auto max-h-[72vh] object-contain"
                  data-testid="detail-image"
                />
              ) : (
                <span className="text-muted-foreground text-sm py-20">Kein Bild</span>
              )}
            </div>
          </div>
        </div>

        {/* data */}
        <div className="md:col-span-7 space-y-6">
          <section className="border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-lg font-medium">Ausgelesene Daten</h2>
            </div>
            <Row label="Lieferant" value={receipt.supplier} testId="detail-supplier" />
            {receipt.invoice_number ? (
              <Row label="Rechnungsnummer" value={receipt.invoice_number} testId="detail-invoice-number" />
            ) : null}
            <Row label="Datum" value={formatDate(receipt.date)} testId="detail-date" />
            <Row
              label="Preis (Brutto)"
              value={formatMoney(receipt.price, receipt.currency)}
              testId="detail-price"
            />
            <Row
              label="MwSt. Betrag"
              value={formatMoney(receipt.vat_amount, receipt.currency)}
              testId="detail-vat"
            />
            {(receipt.tip || 0) > 0 && (
              <>
                <Row
                  label="Trinkgeld"
                  value={formatMoney(receipt.tip, receipt.currency)}
                  testId="detail-tip"
                />
                <Row
                  label="Gesamtbetrag (inkl. Trinkgeld)"
                  value={formatMoney(
                    (receipt.price || 0) + (receipt.tip || 0),
                    receipt.currency
                  )}
                  testId="detail-grand-total"
                />
              </>
            )}
            {receipt.vat_breakdown?.length >= 2 ? (
              <div className="pt-3 space-y-2" data-testid="detail-vat-breakdown">
                <VatBreakdown
                  lines={receipt.vat_breakdown}
                  currency={receipt.currency}
                />
              </div>
            ) : (
              <Row
                label="MwSt. Satz"
                value={receipt.vat_rate ? `${receipt.vat_rate} %` : "—"}
              />
            )}
          </section>

          <section className="border border-border bg-card p-5">
            <h2 className="font-heading text-lg font-medium mb-3">Anreicherung</h2>
            <Row
              label="Zweck"
              value={effectiveValue(receipt.purpose, receipt.purpose_other)}
              testId="detail-purpose"
            />
            <Row
              label="Durchgeführt von"
              value={effectiveValue(receipt.performed_by, receipt.performed_by_other)}
              testId="detail-performed-by"
            />
            <Row
              label="Bezahlt von"
              value={effectiveValue(receipt.paid_by, receipt.paid_by_other)}
              testId="detail-paid-by"
            />
            <Row
              label="Bezahlt mit"
              value={effectiveValue(receipt.paid_with, receipt.paid_with_other)}
              testId="detail-paid-with"
            />
            {receipt.note ? <Row label="Notiz" value={receipt.note} /> : null}
            <Row label="Erfasst am" value={formatDateTime(receipt.created_at)} />
          </section>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Beleg löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Beleg von „{receipt.supplier || "Unbekannt"}&quot; wird dauerhaft
              entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="detail-cancel-delete">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="detail-confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
